import { stateOptions } from '../commonStateTaxData';

export const getStep1Questions = (version = '') => [...employeeInfoQuestions, getFilingStatus(version)];

export const getFilingStatus = (version = '') => {
  const qualifyingWidowerDescription = ['12_20', '12_21'].includes(version)
    ? 'Qualifying widow(er)'
    : 'Qualifying surviving spouse';

  return {
    id: 'filing_status',
    title: 'Please select filing status:',
    is_required: true,
    question_type: 'dropdown',
    options: [
      {
        codeValue: 'single',
        shortName: 'Single',
      },
      {
        codeValue: 'married_filing_jointly',
        shortName: 'Married filing jointly',
      },
      {
        codeValue: 'married_filing_separately',
        shortName: 'Married filing separately',
      },
      {
        codeValue: 'qualifying_widower',
        shortName: qualifyingWidowerDescription,
      },
      {
        codeValue: 'head_of_household',
        shortName: 'Head of household',
      },
    ],
  };
};

export const employeeInfoQuestions = [
  {
    id: 'employee_first_name',
    title: 'First name',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'employee_middle_name',
    title: 'Middle initial',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[a-zA-Z]{1}$',
        customErrorMessage: 'The middle initial can only contain 1 letter.',
      },
    ],
  },
  {
    id: 'employee_last_name',
    title: 'Last name',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'employee_ssn',
    title: 'Social Security number',
    is_required: true,
    placeholder: '9 digits',
    question_type: 'identity',
    identity_type: 'ssn',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^\\d{9}$',
      },
    ],
  },
  {
    id: 'street_address',
    title: 'Home address',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'city_or_town',
    title: 'City or town',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'state',
    title: 'State',
    is_required: true,
    question_type: 'dropdown',
    options: stateOptions,
  },
  {
    id: 'zip_code',
    title: 'Zip code',
    is_required: true,
    question_type: 'short_text',
    placeholder: '5 digits',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^\\d{5}$',
      },
    ],
  },
];

export const step1Part2Questions = [
  {
    id: 'additional_amount',
    title: 'Additional withholding amount per pay period, if any:',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^(0|[1-9][0-9]*)$',
      },
    ],
  },
  {
    id: 'reduced_amount',
    title: 'Reduced withholding amount per pay period, if any:',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^(0|[1-9][0-9]*)$',
      },
    ],
  },
];

export const maritalStatusOptions = {
  married_filing_jointly: [
    {
      id: 'withholding_code',
      is_required: true,
      title: 'Choose the statement that best describes your gross income',
      question_type: 'long_multiple_choice',
      options: [
        {
          codeValue: 'expected_combined_annual_gross_income_lteq_24000',
          shortName:
            'Our expected combined annual gross income is less than or equal to $24,000 or I am claiming exemption under the Military Spouses Residency Relief Act (MSRRA)* and no withholding is necessary.',
        },
        {
          codeValue: 'spouse_is_employed_and_gross_income_gt_24000_lt_100500',
          shortName:
            'My spouse is employed and our expected combined annual gross income is greater than $24,000 and less than or equal to $100,500.',
        },
        {
          codeValue: 'spouse_is_not_employed',
          shortName:
            'My spouse is not employed and our expected combined annual gross income is greater than $24,000.',
        },
        {
          codeValue: 'spouse_is_employed_and_gross_income_gt_100500',
          shortName:
            'My spouse is employed and our expected combined annual gross income is greater than $100,500.',
        },
        {
          codeValue: 'significant_nonwage_income',
          shortName:
            'I have significant nonwage income and wish to avoid having too little tax withheld.',
        },
        {
          codeValue: 'nonresident_of_connecticut',
          shortName:
            'I am a nonresident of Connecticut with substantial other income.',
        },
      ],
    },
  ],
  qualifying_widower: [
    {
      id: 'withholding_code',
      title: 'Choose the statement that best describes your gross income',
      is_required: true,
      question_type: 'long_multiple_choice',
      options: [
        {
          codeValue: 'expected_annual_gross_income_lteq_24000',
          shortName:
            'My expected annual gross income is less than or equal to $24,000 or I am claiming exemption under the MSRRA and no withholding is necessary.',
        },
        {
          codeValue: 'expected_annual_gross_income_gt_24000',
          shortName: 'My expected annual gross income is greater than $24,000.',
        },
        {
          codeValue: 'significant_nonwage_income',
          shortName:
            'I have significant nonwage income and wish to avoid having too little tax withheld.',
        },
        {
          codeValue: 'nonresident_of_connecticut',
          shortName:
            'I am a nonresident of Connecticut with substantial other income.',
        },
      ],
    },
  ],
  married_filing_separately: [
    {
      id: 'withholding_code',
      title: 'Choose the statement that best describes your gross income',
      is_required: true,
      question_type: 'long_multiple_choice',
      options: [
        {
          codeValue: 'expected_annual_gross_income_lteq_12000',
          shortName:
            'My expected annual gross income is less than or equal to $12,000 or I am claiming exemption under the MSRRA and no withholding is necessary.',
        },
        {
          codeValue: 'expected_annual_gross_income_gt_12000',
          shortName: 'My expected annual gross income is greater than $12,000.',
        },
        {
          codeValue: 'significant_nonwage_income',
          shortName:
            'I have significant nonwage income and wish to avoid having too little tax withheld.',
        },
        {
          codeValue: 'nonresident_of_connecticut',
          shortName:
            'I am a nonresident of Connecticut with substantial other income.',
        },
      ],
    },
  ],
  single: [
    {
      id: 'withholding_code',
      title: 'Choose the statement that best describes your gross income',
      is_required: true,
      question_type: 'long_multiple_choice',
      options: [
        {
          codeValue: 'expected_annual_gross_income_lteq_15000',
          shortName:
            'My expected annual gross income is less than or equal to $15,000 and no withholding is necessary.',
        },
        {
          codeValue: 'expected_annual_gross_income_gt_15000',
          shortName: 'My expected annual gross income is greater than $15,000.',
        },
        {
          codeValue: 'significant_nonwage_income',
          shortName:
            'I have significant nonwage income and wish to avoid having too little tax withheld.',
        },
        {
          codeValue: 'nonresident_of_connecticut',
          shortName:
            'I am a nonresident of Connecticut with substantial other income.',
        },
      ],
    },
  ],
  head_of_household: [
    {
      id: 'withholding_code',
      title: 'Choose the statement that best describes your gross income',
      is_required: true,
      question_type: 'long_multiple_choice',
      options: [
        {
          codeValue: 'expected_annual_gross_income_lteq_19000',
          shortName:
            'My expected annual gross income is less than or equal to $19,000 and no withholding is necessary.',
        },
        {
          codeValue: 'expected_annual_gross_income_greater_than_19000',
          shortName: 'My expected annual gross income is greater than $19,000.',
        },
        {
          codeValue: 'significant_nonwage_income',
          shortName:
            'I have significant nonwage income and wish to avoid having too little tax withheld.',
        },
        {
          codeValue: 'nonresident_of_connecticut',
          shortName:
            'I am a nonresident of Connecticut with substantial other income.',
        },
      ],
    },
  ],
};

export const exemptionDropdownQuestion = [
  {
    id: 'state_of_residence_or_domicile',
    title: 'Select your state of legal residence or domicile: ',
    is_required: false,
    question_type: 'dropdown',
    options: stateOptions,
  },
];
